import React, { useState } from 'react';
import PropTypes from 'prop-types';
import R from 'ramda';
import classNames from 'classnames';
import Heading from '@cotyorg/ccx-atoms/Heading';
import {
  trackVideoPlayEvent,
  trackVideoMilestoneEvent,
} from '@cotyorg/ccx-utils/js/tracking/adobe-dtm/events/video';
import { VideoPlayer } from '@cotyorg/ccx-organism-video';
import OptionalSection from '@cotyorg/ccx-utils/js/components/OptionalSection';
import ProductSteps from '../../molecules/ProductSteps';
import ProductInfo from '../../organisms/ProductNew/ProductNewInfo';
import ProductGalleryThumbnails from '../../organisms/ProductNew/ProductGalleryThumbnails';
import { isSmallBreakpoint } from '../../utils';
import StandardLayout from '../../templates/StandardLayout';
import HeroQuarterImageContent from '../../organisms/HeroQuarterImageContent';
import { schema } from './helpers';
import VtoModal from '../../organisms/VtoModal';
import RelatedProducts from '../../organisms/ProductNew/RelatedProducts';
import Lookbook from '../../organisms/ProductNew/Lookbook';
import Tips from '../../organisms/ProductNew/Tips';

const generateContainerClassNames = (isSmall) =>
  classNames('grid-container', {
    full: isSmall,
  });

const shouldShowSteps = ({ productSteps }) =>
  R.path(['steps', 'length'], productSteps);

const generateOptionalClassNames = ({ isStepSection, isVideoSection }) =>
  classNames('cell', 'small-16', {
    'medium-7': isStepSection && isVideoSection,
    'medium-14': !(isStepSection && isVideoSection),
  });

const ProductNew = ({
  sectionHeadings,
  productDetails,
  productSteps,
  retailers,
  breadcrumb,
  currentBreakpoint,
  categoryTitle,
  subcategoryTitle,
  productSwatch,
  shapes,
  videos,
  shouldUseBazaar,
  customRetailer,
  currentUrl,
  tracking,
  hidePrice,
  selectedSKU,
  vto,
  channelAdvisorWidget,
  customHeroes,
}) => {
  const [vtoButton, setVtoButton] = useState(null);
  const [productSku, setProductSku] = useState(selectedSKU);

  const isVideoSection = !!R.path(['mainVideo', 'url'], videos);
  const isStepSection = shouldShowSteps({ productSteps }) > 0;
  const optionalClassNames = generateOptionalClassNames({
    isStepSection,
    isVideoSection,
  });

  return (
    <StandardLayout>
      <div itemScope itemType="http://schema.org/Product">
        <div className="product-page-new">
          <div className="product-details">
            <div
              className={generateContainerClassNames(
                isSmallBreakpoint({ currentBreakpoint })
              )}
            >
              <div className="grid-x grid-padding-x">
                <div className="cell xsmall-16 medium-8">
                  {isSmallBreakpoint({ currentBreakpoint }) &&
                    R.path(['logo', 'url'], productDetails) && (
                      <div className="product-logo">
                        <img
                          src={R.path(['logo', 'url'], productDetails)}
                          alt={R.path(['logo', 'title'], productDetails)}
                        />
                      </div>
                    )}
                  <ProductGalleryThumbnails
                    headingLevel={3}
                    rightChevronIconSrc="/ccx-files/assets/chevron-right.svg"
                    leftChevronIconSrc="/ccx-files/assets/chevron-left.svg"
                    carouselsStoreDataKey="productGallery"
                    renderCustomElement={() =>
                      !!vto && (
                        <VtoModal
                          channelAdvisorWidget={channelAdvisorWidget}
                          setOpenButtonRef={(ref) => setVtoButton(ref)}
                          triggerButton={R.prop('galleryButton', vto)}
                          actualProductSku={productSku}
                          disabledEmailSharing={R.prop(
                            'disabledEmailSharing',
                            vto
                          )}
                        />
                      )
                    }
                  />
                </div>

                <div className="cell xsmall-16 medium-8">
                  <ProductInfo
                    currentBreakpoint={currentBreakpoint}
                    breadcrumb={breadcrumb}
                    sectionHeadings={sectionHeadings}
                    productDetails={productDetails}
                    retailers={retailers}
                    categoryTitle={categoryTitle}
                    subcategoryTitle={subcategoryTitle}
                    productSwatch={productSwatch}
                    shapes={shapes}
                    showRating={shouldUseBazaar}
                    customRetailer={customRetailer}
                    hidePrice={hidePrice}
                    isVtoEnabled={!!vto}
                    vtoButton={
                      vtoButton &&
                      (() => (
                        <button
                          type="button"
                          className="vto__button"
                          onClick={() => {
                            vtoButton.click();
                          }}
                        >
                          try on
                        </button>
                      ))
                    }
                  />
                </div>
              </div>
            </div>
          </div>

          {(R.path(['heroQuarterImageContent', 'imageSrc'], customHeroes) ||
            R.path(
              ['heroQuarterImageContent', 'description'],
              customHeroes
            )) && (
            <div className="product-hero">
              <div className="grid-container">
                <div className="grid-x align-center">
                  <div className="cell xsmall-16 medium-14">
                    <HeroQuarterImageContent
                      customHeroesStoreDataKey="heroQuarterImageContent"
                      columnsConfig={{
                        leftColumn: ['cell', 'xsmall-16', 'large-8'],
                        rightColumn: ['cell', 'xsmall-16', 'large-8'],
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {!!shouldShowSteps({ productSteps }) && (
            <div className="product-steps">
              <div className="grid-container product-steps__content">
                <div className="grid-x grid-padding-x align-center">
                  <div className="cell xsmall-16">
                    <Heading
                      level={3}
                      extraClass="product-steps__heading text--center"
                    >
                      {R.path(['application_new', 'heading'], sectionHeadings)}
                    </Heading>
                  </div>

                  {isVideoSection && (
                    <div className={optionalClassNames}>
                      <div className="how-to-article-youtube-video">
                        <div className="youtube-video">
                          <VideoPlayer
                            videosStoreDataKey="mainVideo"
                            showControls={false}
                            title={R.path(['mainVideo', 'name'], videos)}
                            shouldUseDefaultTitle
                            onProgress={({
                              url,
                              duration,
                              progress,
                              videoMilestone,
                              title,
                            }) =>
                              trackVideoMilestoneEvent({
                                url,
                                duration,
                                progress,
                                videoMilestone,
                                title,
                              })
                            }
                            onPlay={({ url, duration, progress, title }) =>
                              trackVideoPlayEvent({
                                url,
                                duration,
                                progress,
                                title,
                              })
                            }
                            config={{
                              youtube: {
                                playerVars: {
                                  controls: 1,
                                },
                                embedOptions: {
                                  host: 'https://www.youtube-nocookie.com',
                                },
                              },
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {isStepSection && (
                    <div className={optionalClassNames}>
                      <div className="grid-x grid-padding-x">
                        <div className="cell xsmall-16 medium-15 medium-offset-1">
                          <ProductSteps
                            currentBreakpoint={currentBreakpoint}
                            sectionHeadings={sectionHeadings}
                            productSteps={productSteps}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {R.prop('tips', productDetails) && (
            <Tips
              tips={R.prop('tips', productDetails)}
              currentBreakpoint={currentBreakpoint}
            />
          )}

          {R.prop('lookbook', productDetails) && (
            <Lookbook
              lookbook={R.prop('lookbook', productDetails)}
              currentBreakpoint={currentBreakpoint}
            />
          )}

          <div className="grid-container">
            <div className="grid-x align-center">
              <div className="cell xsmall-14">
                <RelatedProducts sectionHeadings={sectionHeadings} />
              </div>
            </div>
          </div>

          <OptionalSection
            renderCondition={shouldUseBazaar}
            section={() => (
              <>
                <div
                  className="product-reviews product-reviews-new"
                  id="reviews"
                >
                  <div className="grid-container">
                    <div className="product-reviews__box">
                      <div
                        data-bv-show="reviews"
                        data-bv-product-id={R.prop('bvid', productDetails)}
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
          />
        </div>

        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              schema({
                sectionHeadings,
                productDetails,
                retailers,
                currentUrl,
                tracking,
              })
            ),
          }}
        />
      </div>
    </StandardLayout>
  );
};

ProductNew.propTypes = {
  channelAdvisorWidget: PropTypes.element,
  productDetails: PropTypes.shape({
    name: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    shadeSelectLabel: PropTypes.string.isRequired,
    noReviewsLabel: PropTypes.string.isRequired,
    readReviewsLabel: PropTypes.string.isRequired,
    showMoreRetailersLabel: PropTypes.string.isRequired,
    showLessRetailersLabel: PropTypes.string.isRequired,
    buttonText: PropTypes.string.isRequired,
    tabs: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string.isRequired,
        content: PropTypes.string.isRequired,
      })
    ),
  }),
  productSteps: PropTypes.shape({
    imageSrc: PropTypes.string,
    imageSrcSet: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        src: PropTypes.string,
      })
    ),
  }),
  retailers: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      price: PropTypes.string,
      currency: PropTypes.string,
    })
  ),
  sectionHeadings: PropTypes.shape({
    relatedProducts: PropTypes.shape({
      heading: PropTypes.string,
    }),
    productSteps: PropTypes.shape({
      heading: PropTypes.string,
    }),
    retailers: PropTypes.shape({
      heading: PropTypes.string,
    }),
  }),
  breadcrumb: PropTypes.arrayOf(
    PropTypes.shape({
      link: PropTypes.string,
      label: PropTypes.string.isRequired,
    })
  ),
  currentBreakpoint: PropTypes.string.isRequired,
  categoryTitle: PropTypes.string,
  subcategoryTitle: PropTypes.string,
  productSwatch: PropTypes.shape({
    swatches: PropTypes.arrayOf(
      PropTypes.shape({
        sku: PropTypes.string,
        itemName: PropTypes.string,
        skuColor: PropTypes.string,
      })
    ),
  }),
  shapes: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string,
      title: PropTypes.string,
    })
  ),
  videos: PropTypes.objectOf(PropTypes.object),
  shouldUseBazaar: PropTypes.bool,
  customRetailer: PropTypes.shape({
    title: PropTypes.string,
    button: PropTypes.shape({
      title: PropTypes.string,
      link: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
    }),
    retailer: PropTypes.shape({
      name: PropTypes.string.isRequired,
      currencyCode: PropTypes.string,
      currency: PropTypes.string,
    }),
  }),
  currentUrl: PropTypes.string.isRequired,
  tracking: PropTypes.shape({
    currencyCode: PropTypes.string,
    product: PropTypes.arrayOf(
      PropTypes.shape({
        attributes: PropTypes.shape({
          productUrl: PropTypes.string,
        }),
      })
    ),
  }),
  hidePrice: PropTypes.bool,
  selectedSKU: PropTypes.string,
  vto: PropTypes.instanceOf(PropTypes.object),
  customHeroes: PropTypes.instanceOf(PropTypes.object),
};

export default ProductNew;
